import { RpcError, RpcStatus } from '@protobuf-ts/runtime-rpc'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { debounce } from 'components/no-ui/helpers/debounce'
import { AllowedRefs, convertToRefString, getRefString, WebsocketNotifyClient } from 'lib/websocket-notify'
import { websocketFilterManager } from 'lib/ws/services/websocket-filter-manager'
import { ChangedRefEventType } from 'pbts/c1/api/websocketnotify/v1/websocketnotify'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { InvokePagedListRPC, rpcNotReadyErr } from 'redux_toolkit/lib/createPagedListApi'
import {
    ACTIONS,
    ERRORED,
    INVALIDATE_TAG,
    INVALIDATE_TAGS,
    LOADING,
    MARK_DIRTY,
    MODIFY_LIST,
    RESET_LIST,
    RESET_LIST_LOADING,
    SUBSCRIBE_LIST,
    UNSUBSCRIBE,
} from './actionTypes'
import { Flags } from './GRPCSubscriptionObject'
import { ReduxListCache, ReduxListState, serializedError, SerializedErrorType } from './reducer'
import { LIST_SUBSCRIPTIONS } from './subscriptions'
import { compositeKey, ListableRequest, ListableResponse } from './utils'

export type ListFlags<Response> = Omit<Flags<Response>, 'notify'> & {
    notify?: WebsocketNotifyClient
    initialPagesToLoad?: number
    pageSize?: number
    tag?: string
    actionable?: boolean
    // If enabled, we don't check if the ref from notify exists in our table cache. We invalidate the whole list if autoReload is true.
    disableEssentialTableExistenceCheck?: boolean
    autoReload?: boolean
}

export class GRPCSubscriptionPagedList<Request extends ListableRequest, Response extends ListableResponse> {
    private notifyAborter: Record<string, AbortController>
    private aborter?: AbortController // equivalent to a request in flight
    private pollerAborter?: AbortController // same as above, put for the optional polling
    private retryTimer?: number // retrying reqs on error
    private notifyRetryTimer?: number // retrying reqs on error
    private destructionTimer?: number // destructor on the GRPCSubcription
    private pollingInterval?: number // if we are polling refs
    private pollingTimerTime?: number //
    private lastPollTimeForRef: Record<string, number> // last time we polled for a ref
    private pollingRefs?: Array<AllowedRefs> // refs we are polling
    private deNotify?: () => void // unsubscribe from notificaitons
    private ref_count = 0
    private websocketNotify?: WebsocketNotifyClient
    private transformResponse?: (r: Response) => unknown
    private maxRetries: number
    private evictionAfterSeconds: number
    private pageTokens: Array<string> = []
    private hasAnotherPage = true
    private disableEssentialTableExistenceCheck?: boolean
    private autoReload?: boolean
    private debouncedInvalidateInternal: () => void
    private pendingInvalidation = false
    private isNotifySubscribed = false
    private pendingNotifyRefs: Array<{ ref: AllowedRefs; refString: string }> = []
    private pendingNotifyParams: { force: boolean; availableRetries: number; wait: number } | null = null
    private notifyDebounceTimer?: number
    public tag?: string
    public pageSize: number
    public loadedNumberOfPages = -1

    constructor(
        private dispatch_: ThunkDispatch<ReduxListState<object>, never, UnknownAction>,
        private _getState: () => ReduxListState<object>,
        public reducer: string,
        public cacheKey: string,
        private invokeRPC_: InvokePagedListRPC<Request, Response>,
        opts?: ListFlags<Response>,
    ) {
        this.transformResponse = opts?.transformResponse
        this.websocketNotify = opts?.notify
        this.maxRetries = opts?.maxRetries || 3
        // Evict almost immediately by default to make list pages go
        this.evictionAfterSeconds = (opts?.evictionAfterSeconds || 1) * 1000
        this.tag = opts?.tag
        this.pageSize = opts?.pageSize || 50
        this.notifyAborter = {}
        this.disableEssentialTableExistenceCheck = opts?.disableEssentialTableExistenceCheck
        this.autoReload = opts?.autoReload
        this.lastPollTimeForRef = {}

        this.debouncedInvalidateInternal = debounce(this.invalidateInternal.bind(this), 1000, 5000)
    }

    get state(): ReduxListCache<object> {
        return this._getState().toolkit[this.reducer][this.cacheKey]
    }

    get key(): string {
        return compositeKey(this.reducer, this.cacheKey)
    }

    get alive(): boolean {
        return this.ref_count > 0
    }

    dispatch = ({
        type,
        response,
        error,
        tag,
        refString,
        refStrings,
        canLoadMore,
        page,
        shouldInvalidateList,
        isLoading,
        lastPollTimeForRef,
    }: {
        type: ACTIONS // maybe list actions?
        response?: unknown
        error?: SerializedErrorType
        status?: RpcStatus
        tag?: string
        refString?: string
        refStrings?: string[]
        canLoadMore?: boolean
        page?: number
        shouldInvalidateList?: boolean
        isLoading?: boolean
        lastPollTimeForRef?: Record<string, number>
    }): ReturnType<typeof this.dispatch_> =>
        this.dispatch_({
            type,
            reducer: this.reducer,
            response,
            error,
            cacheKey: this.cacheKey,
            now: Date.now(),
            tag,
            refString,
            refStrings,
            canLoadMore,
            page,
            shouldInvalidateList,
            isLoading,
            lastPollTimeForRef,
        })

    subscribe = async (): Promise<unknown> => {
        this.ref_count += 1

        clearInterval(this.destructionTimer)
        delete this.destructionTimer

        this.dispatch({ type: SUBSCRIBE_LIST, tag: this.tag })
        const result = this.getPage(0)
        GRPCSubscriptionPagedList.notifyFilterManager()
        return result
    }

    unsubscribe = (): boolean => {
        this.ref_count = Math.max(0, this.ref_count - 1)
        if (this.alive || this.destructionTimer) {
            return false
        }

        this.destructionTimer = setTimeout(this.destroy, this.evictionAfterSeconds) as unknown as number
        return true
    }

    invalidate = (): boolean => {
        this.pendingInvalidation = true
        this.debouncedInvalidateInternal()
        return true
    }

    private invalidateInternal = (): void => {
        // Clear the pending invalidation flag since we're now executing
        this.pendingInvalidation = false

        // Enable watchInserts override since we're refreshing the list
        websocketFilterManager.setWatchInsertsForSubscription(this.key, true)

        this.hasAnotherPage = true
        this.loadedNumberOfPages = 0
        this.pageTokens = []
        this.dispatch({
            type: RESET_LIST_LOADING,
            tag: this.tag,
            isLoading: !this.autoReload,
        })
        this.invokeRPC(true, this.maxRetries, 0, undefined, true)
    }

    getPage = (pageNumber: number, force = false): Promise<unknown> => {
        return this.invokeRPC(force, this.maxRetries, pageNumber)
    }

    invokeRPC = async (
        force: boolean,
        availableRetries: number,
        pageTokenIndex: number,
        w = 1,
        resetList = false,
    ): Promise<unknown> => {
        if (!this.alive) {
            return
        }
        if (pageTokenIndex > this.loadedNumberOfPages && !this.hasAnotherPage) {
            // could load the last page??
            return
        }

        if (pageTokenIndex <= this.loadedNumberOfPages && !force) {
            return
        }

        if (this.aborter) {
            if (!force) {
                return
            }
            // cancel request in flight and start over (useful on notification)
            // does this make sense for lists though?
            // how do we know it's for this page??
            this.aborter.abort()
        }
        // abundance of caution
        clearTimeout(this.retryTimer)
        delete this.retryTimer

        this.dispatch({
            type: LOADING,
        })

        let response: undefined | Awaited<ReturnType<InvokePagedListRPC<Request, Response>>>
        let error: undefined | RpcError

        this.aborter = new AbortController()
        try {
            const pt = pageTokenIndex === 0 ? '' : this.pageTokens[pageTokenIndex - 1]
            response = await this.invokeRPC_(pt, this.pageSize, {
                abort: this.aborter.signal,
            })
        } catch (e) {
            // RpcError seems to be enforced with words, not the type system :-/
            error = (e || new Error('!?!')) as unknown as RpcError
        }

        delete this.aborter

        // TODO: should we really throw away the response if we aren't alive?
        if (!this.alive || error?.code === 'CANCELLED') {
            // subscription ended, or we aborted the call (handled on the other stack frame)
            return
        }

        if (error) {
            if (error === rpcNotReadyErr) {
                return
            }

            this.dispatch({ type: ERRORED, error: serializedError(error) })
            if (availableRetries >= 1) {
                const timeout = 1000 * Math.min(w, 120)
                this.retryTimer = setTimeout(
                    () => this.invokeRPC(force, availableRetries - 1, pageTokenIndex, w * 2),
                    timeout,
                ) as unknown as number
            }
            return
        }

        // Make TS happy
        if (!response) {
            throw new Error('no response!?')
        }

        this.loadedNumberOfPages = this.loadedNumberOfPages > pageTokenIndex ? this.loadedNumberOfPages : pageTokenIndex

        const token = response.response.nextPageToken
        if (!token) {
            this.hasAnotherPage = false
        } else {
            this.pageTokens.push(token)
        }

        if (this.websocketNotify && this.tag && !this.isNotifySubscribed) {
            this.websocketNotify.subscribe(
                [this.tag],
                this.cacheKey,
                (ref: AllowedRefs, eventType: ChangedRefEventType, lastUpdatedAt: Timestamp | undefined) => {
                    this.onNotify(true, 2, 1, ref, eventType, lastUpdatedAt)
                },
            )
            this.isNotifySubscribed = true
        }
        const result = this.dispatch({
            type: resetList ? RESET_LIST : MODIFY_LIST,
            status: response.status,
            response: this.transformResponse ? this.transformResponse(response.response) : response.response.list,
            canLoadMore: this.hasAnotherPage,
            page: pageTokenIndex,
        })
        GRPCSubscriptionPagedList.notifyFilterManager()
        return result
    }

    // Deprecated: don't use this :-/
    poll = (refs: Array<AllowedRefs>, interval: number, cancelExisting: boolean): void => {
        this.pollingRefs = refs
        this.pollingTimerTime = interval

        if (this.pollerAborter) {
            if (!cancelExisting) {
                return
            }
            this.pollerAborter.abort()
            delete this.pollerAborter
        }

        clearInterval(this.pollingInterval)
        this.pollingInterval = setInterval(async () => {
            if (!this.pollingRefs) {
                // nothing to do...
                clearInterval(this.pollingInterval)
                delete this.pollingInterval
                return
            }

            // This will happen if a request takes longer than the polling interval
            if (this.pollerAborter) {
                return
            }

            let response: undefined | Awaited<ReturnType<InvokePagedListRPC<Request, Response>>>
            try {
                this.pollerAborter = new AbortController()
                // set last poll time for ref
                const d = new Date().getTime()
                const localPollTimeMap = {}
                this.pollingRefs.forEach((r) => {
                    localPollTimeMap[getRefString(r)] = d
                })
                this.lastPollTimeForRef = { ...this.lastPollTimeForRef, ...localPollTimeMap }
                response = await this.invokeRPC_(
                    '',
                    this.pollingRefs.length,
                    { abort: this.pollerAborter.signal },
                    this.pollingRefs,
                )
            } catch (e) {
                // maybe stop after N errors???
                console.error(e)
                delete this.pollerAborter
                if ((e as unknown as RpcError)?.code !== 'CANCELLED') {
                    console.error(e)
                }
                return
            }
            delete this.pollerAborter

            const resp = this.transformResponse
                ? (this.transformResponse(response.response) as Array<unknown>)
                : response.response.list

            this.dispatch({
                type: INVALIDATE_TAGS,
                response: resp,
                refStrings: refs.map(getRefString),
                lastPollTimeForRef: this.lastPollTimeForRef,
            })
        }, this.pollingTimerTime) as unknown as number
    }

    getNotifyResponse = async (
        force: boolean,
        availableRetries: number,
        wait = 1,
        refString: string,
        ref: AllowedRefs,
    ): Promise<Array<unknown>> => {
        if (this.notifyAborter[refString]) {
            if (!force) {
                throw new Error('request in flight')
            }
            // cancel request in flight and start over (useful on notification)
            // does this make sense for lists though?
            // how do we know it's for this page??
            this.notifyAborter[refString].abort()
        }

        let response: undefined | Awaited<ReturnType<InvokePagedListRPC<Request, Response>>>
        let error: undefined | RpcError

        this.notifyAborter[refString] = new AbortController()
        try {
            response = await this.invokeRPC_(
                '',
                this.pageSize,
                {
                    abort: this.notifyAborter[refString].signal,
                },
                [ref],
            )
        } catch (e) {
            error = e as RpcError
        }

        delete this.notifyAborter[refString]

        if (!this.alive || error?.code === 'CANCELLED') {
            // subscription ended, or we aborted the call (handled on the other stack frame)
            const err = new Error('subscription ended')
            err.name = 'SubscriptionEndedError'
            throw err
        }

        if (error) {
            const errorSerialized: SerializedErrorType = {
                message: error.message,
                name: error.name,
                ...(error.code && { code: error.code }),
            }
            this.dispatch({ type: ERRORED, error: errorSerialized })
            if (availableRetries >= 1) {
                this.notifyRetryTimer = setTimeout(
                    this.getNotifyResponse,
                    wait * 1000,
                    force,
                    availableRetries - 1,
                    Math.min(wait * 2, 120),
                    refString,
                    ref,
                ) as unknown as number
            }
            throw error
        }

        if (response === undefined) {
            throw new Error('no response!')
        }

        return this.transformResponse
            ? (this.transformResponse(response.response) as Array<unknown>)
            : response.response.list
    }

    private processBatchedNotifyRefs = async (): Promise<Array<unknown>> => {
        if (this.pendingNotifyRefs.length === 0 || !this.pendingNotifyParams) {
            return []
        }

        const refsToProcess = [...this.pendingNotifyRefs]
        const params = { ...this.pendingNotifyParams }

        // Clear the pending state
        this.pendingNotifyRefs = []
        this.pendingNotifyParams = null
        clearTimeout(this.notifyDebounceTimer)
        delete this.notifyDebounceTimer

        if (!this.alive) {
            return []
        }

        const batchKey = `batch-${Date.now()}`
        this.notifyAborter[batchKey] = new AbortController()

        let response: undefined | Awaited<ReturnType<InvokePagedListRPC<Request, Response>>>
        let error: undefined | RpcError

        try {
            response = await this.invokeRPC_(
                '',
                this.pageSize,
                {
                    abort: this.notifyAborter[batchKey].signal,
                },
                refsToProcess.map((r): AllowedRefs => r.ref),
            )
        } catch (e) {
            error = e as RpcError
        }

        delete this.notifyAborter[batchKey]

        if (!this.alive || error?.code === 'CANCELLED') {
            const err = new Error('subscription ended')
            err.name = 'SubscriptionEndedError'
            throw err
        }

        if (error) {
            if (params.availableRetries >= 1) {
                this.notifyRetryTimer = setTimeout((): void => {
                    // Re-enqueue all refs for retry
                    refsToProcess.forEach((r): void => {
                        this.enqueueBatchedNotifyRequest(
                            params.force,
                            params.availableRetries - 1,
                            Math.min(params.wait * 2, 120),
                            r.refString,
                            r.ref,
                        )
                    })
                }, params.wait * 1000) as unknown as number
            }
            throw error
        }

        if (response === undefined) {
            throw new Error('no response!')
        }

        return this.transformResponse
            ? (this.transformResponse(response.response) as Array<unknown>)
            : response.response.list
    }

    enqueueBatchedNotifyRequest = async (
        force: boolean,
        availableRetries: number,
        wait: number,
        refString: string,
        ref: AllowedRefs,
    ): Promise<Array<unknown>> => {
        // Add to the pending queue (dedupe by refString)
        const existingIndex = this.pendingNotifyRefs.findIndex((r): boolean => r.refString === refString)
        if (existingIndex === -1) {
            this.pendingNotifyRefs.push({ ref, refString })
        }
        this.pendingNotifyParams = { force, availableRetries, wait }

        // If we have 20 refs, process immediately
        if (this.pendingNotifyRefs.length >= 20) {
            return this.processBatchedNotifyRefs()
        }

        // Otherwise, set/reset the 1 second timer
        clearTimeout(this.notifyDebounceTimer)
        return new Promise((resolve, reject): void => {
            this.notifyDebounceTimer = setTimeout((): void => {
                this.processBatchedNotifyRefs().then(resolve).catch(reject)
            }, 1000) as unknown as number
        })
    }

    onNotify = async (
        force: boolean,
        availableRetries: number,
        wait = 1,
        ref: AllowedRefs,
        eventType: ChangedRefEventType,
        lastUpdatedAt: Timestamp | undefined,
    ): Promise<unknown> => {
        if (!this.alive || !ref || eventType === ChangedRefEventType.UNSPECIFIED || !this.tag) {
            return
        }

        const refString = getRefString(ref)
        const cachedObject = this.state
        const tags = cachedObject.metadata.tags

        // check if the ref exists in the table
        if (!tags || !tags[refString]) {
            // If it doesn't exist in the table and is inserted OR
            // disableEssentialTableExistenceCheck is true,
            // just prompt to invalidate the whole list.
            // We don't know where in the list this item should be inserted in
            if (this.disableEssentialTableExistenceCheck || eventType === ChangedRefEventType.INSERT) {
                if (this.autoReload) {
                    // If we already have a pending invalidation, skip the expensive getNotifyResponse call
                    // and just trigger another invalidate (which will be debounced)
                    if (this.pendingInvalidation) {
                        this.invalidate()
                        return
                    }

                    let resp: Array<unknown>
                    try {
                        resp = await this.enqueueBatchedNotifyRequest(force, availableRetries, wait, refString, ref)
                    } catch (e) {
                        console.error(e)
                        return
                    }
                    if (resp.length >= 1) {
                        this.invalidate()
                    } else if (this.disableEssentialTableExistenceCheck) {
                        console.warn('ws-notify: disableEssentialTableExistenceCheck: invalid number responses from search call', resp.length, resp)
                    }
                    return
                }
                // Disable watchInserts for this subscription when marking dirty
                websocketFilterManager.setWatchInsertsForSubscription(this.key, false)
                return this.dispatch({
                    type: MARK_DIRTY,
                })
            }
            return
        }

        const [x, y] = tags[refString]
        const cached = cachedObject.data[x][y]

        const refStringFromCache = convertToRefString(cached, this.tag)
        if (refStringFromCache !== refString) {
            console.warn("LIST: tag doesn't match ref of the item to update")
            return
        }

        if (lastUpdatedAt && cached.updatedAt) {
            try {
                const lastUpdatedTimeFromNotify = lastUpdatedAt.seconds * 1000 + lastUpdatedAt.nanos / 1000000
                const lastUpdatedTimeInCache = cached.updatedAt.seconds * 1000 + cached.updatedAt.nanos / 1000000
                if (lastUpdatedTimeInCache >= lastUpdatedTimeFromNotify) {
                    return
                }
            } catch (e) {
                console.warn('Error comparing time updating from notify', lastUpdatedAt)
            }
        }

        // If we already have a pending invalidation and autoReload is enabled,
        // skip the expensive getNotifyResponse call since the whole list will be refreshed anyway
        if (this.pendingInvalidation && this.autoReload) {
            return
        }

        let resp: Array<unknown>
        try {
            resp = await this.getNotifyResponse(force, availableRetries, wait, refString, ref)
        } catch (e) {
            if (e instanceof Error && e.name === 'SubscriptionEndedError') {
                console.warn(e.message)
                return
            }
            console.error(e)
            return
        }

        if (resp.length > 1) {
            console.warn('ws-notify: invalid number responses from search call', resp.length, resp)
            return
        }

        // If deleted and the ref exists in the table, just prompt to invalidate the whole list
        if (resp.length === 0) {
            if (this.autoReload) {
                this.invalidate()
                return
            }
            // Disable watchInserts for this subscription when marking dirty
            websocketFilterManager.setWatchInsertsForSubscription(this.key, false)
            return this.dispatch({
                type: MARK_DIRTY,
            })
        }

        return this.invalidateTag(refString, resp[0])
    }

    destroy = (): void => {
        if (this.alive) {
            console.warn('tried to destroy an live subscription', this.key, this.ref_count)
            return
        }

        if (this.deNotify) {
            this.deNotify()
            delete this.deNotify
        }

        if (this.aborter) {
            this.aborter.abort()
            delete this.aborter
        }

        delete this.pollingRefs
        if (this.pollerAborter) {
            this.pollerAborter.abort()
            delete this.pollerAborter
        }
        for (const aborter of Object.values(this.notifyAborter)) {
            aborter.abort()
        }

        this.notifyAborter = {}
        this.pendingNotifyRefs = []
        this.pendingNotifyParams = null

        clearTimeout(this.destructionTimer)
        clearTimeout(this.retryTimer)
        clearTimeout(this.notifyRetryTimer)
        clearTimeout(this.notifyDebounceTimer)
        clearInterval(this.pollingInterval)

        delete this.destructionTimer
        delete this.retryTimer
        delete this.notifyRetryTimer
        delete this.notifyDebounceTimer
        delete this.pollingInterval

        delete LIST_SUBSCRIPTIONS[this.key]

        if (this.tag) {
            this.websocketNotify?.unsubscribe([this.tag], this.cacheKey)
            // Clear the subscription flag to allow re-subscription
            this.isNotifySubscribed = false
        }

        // Clean up watchInserts tracking when subscription is destroyed
        websocketFilterManager.removeSubscriptionFromTracking(this.key)

        this.dispatch({
            type: UNSUBSCRIBE,
        })

        GRPCSubscriptionPagedList.notifyFilterManager()
    }

    invalidateTag(refString: string, response: unknown): void {
        this.dispatch({
            type: INVALIDATE_TAG,
            response,
            refString,
        })
    }

    private static notifyFilterManager(): void {
        websocketFilterManager.onSubscriptionsChanged()
    }
}
